
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import AdminMelPanel from '@/admin/components/mel/AdminMelPanel';
import CompanyPausePanel from '@/admin/components/pause/CompanyPausePanel';
import MerchantAccountsPanel from '@/admin/components/merchant-accounts/MerchantAccountsPanel';
import CompanyCardsPanel from '@/admin/components/company-cards/CompanyCardsPanel';
import AdminAcquirerDataSection from '@/admin/components/acquirer-data/AdminAcquirerDataSection';
import TransactionsExportPanel from '@/admin/components/transactions-export/TransactionsExportPanel';
import { formatRut, isValidRut } from '@/lib/rut';
import { formatPhoneForDisplay } from '@/lib/phone';

interface Signer { id: string; name: string; email: string }

interface CompanyDetail {
  id: string;
  taxId: string | null;
  businessName: string | null;
  companyType: string | null;
  enabled: boolean;
  signers: Signer[];
  userEmail: string | null;
  phone: string | null;
  documents: {
    deed: { key: string | null; fileName: string | null; reviewStatus: string | null };
    powerOfAttorney: { key: string | null; fileName: string | null; reviewStatus: string | null };
    identityDocument: { key: string | null; fileName: string | null; reviewStatus: string | null };
    signedPromissoryNoteKey: string | null;
  };
  acquirers: {
    acquirerId: string; slug: string | null; name: string | null; integrationStatus: string;
    activatedAt: string | null; cartaPoderKey: string | null; modificacionCuentaKey: string | null; lastSyncAt: string | null;
  }[];
  bankAccount: { accountNumber: string; accountType: string; bankCode: string; holderRut: string | null; holderEmail: string | null } | null;
  hasSiiCredentials: boolean;
  activationStatus: {
    conexionSii: { completedAt: string } | null;
    credencialesAdquirente: { completedAt: string } | null;
    firmaPagare: { completedAt: string } | null;
    cuentaBancaria: { completedAt: string } | null;
    terminos: { completedAt: string } | null;
    lupsActivation?: { tc: 'pending' | 'complete'; completedAt: string | null };
    scrapeCuentas?: { status: 'pending' | 'triggered' | 'complete'; detectedCount: number; completedAt: string | null };
  };
  createdAt: string;
}

const ACTIVATION_STEPS: { key: keyof CompanyDetail['activationStatus']; label: string; description: string; icon: string }[] = [
  { key: 'conexionSii', label: 'Conexion SII', description: 'Facturacion electronica', icon: 'receipt_long' },
  { key: 'credencialesAdquirente', label: 'Credenciales Adquirente', description: 'Origen de tus ventas', icon: 'credit_card' },
  { key: 'firmaPagare', label: 'Firma de Pagare', description: 'Respaldo financiero', icon: 'gavel' },
  { key: 'cuentaBancaria', label: 'Cuenta Bancaria', description: 'Para recibir fondos', icon: 'account_balance' },
  { key: 'lupsActivation', label: 'Cobros automáticos (PAT)', description: 'Proveedor confirmó ≥1 tarjeta activa', icon: 'autorenew' },
  { key: 'scrapeCuentas', label: 'Pago automático de cuentas (PAC)', description: 'Mandato bancario para cobro automático', icon: 'account_balance_wallet' },
  { key: 'terminos', label: 'Terminos y Condiciones', description: 'Aceptacion de uso', icon: 'task_alt' },
];

function isStepComplete(key: keyof CompanyDetail['activationStatus'], status: CompanyDetail['activationStatus'][keyof CompanyDetail['activationStatus']]): boolean {
  if (key === 'lupsActivation') return (status as { tc?: string } | null)?.tc === 'complete';
  if (key === 'scrapeCuentas') return (status as { status?: string } | null)?.status === 'complete';
  return !!status;
}

function StatusDot({ status }: { status: string | null }) {
  const config: Record<string, { color: string; label: string }> = {
    approved: { color: 'bg-emerald-500', label: 'Aprobado' },
    pending: { color: 'bg-amber-500', label: 'Pendiente' },
    rejected: { color: 'bg-red-500', label: 'Rechazado' },
  };
  const c = status ? config[status] : null;
  if (!c) return <span className="text-xs text-slate-400">Sin subir</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
      <span className={`w-2 h-2 rounded-full ${c.color}`} />
      {c.label}
    </span>
  );
}

function InfoItem({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm text-slate-800 dark:text-slate-200 ${mono ? 'font-mono' : ''}`}>{value || <span className="text-slate-400">—</span>}</p>
    </div>
  );
}

function SectionCard({ title, icon, children, description }: { title: string; icon: string; children: React.ReactNode; description?: string }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <span className="material-symbols-outlined text-[18px] text-slate-500">{icon}</span>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
          {description && <p className="text-[11px] text-slate-500">{description}</p>}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function CompanyDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = searchParams.get('id');

  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [signers, setSigners] = useState<Signer[]>([]);
  const [taxId, setTaxId] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [promissoryNoteFile, setPromissoryNoteFile] = useState<File | null>(null);
  const [mandateFiles, setMandateFiles] = useState<Record<string, { cartaPoder: File | null; modificacion: File | null }>>({});

  function setMandateFile(slug: string, kind: 'cartaPoder' | 'modificacion', file: File | null) {
    setMandateFiles((prev) => ({
      ...prev,
      [slug]: { cartaPoder: prev[slug]?.cartaPoder ?? null, modificacion: prev[slug]?.modificacion ?? null, [kind]: file },
    }));
  }
  // Sólo retenemos el username en memoria. El password NUNCA se guarda en estado
  // ni se renderiza en el DOM: el botón "copiar clave" hace fetch puntual al
  // endpoint dedicado, escribe al clipboard y descarta la variable.
  const [siiCredentials, setSiiCredentials] = useState<{ username: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => { if (companyId) loadCompany(); }, [companyId]);

  async function loadCompany() {
    if (!companyId) return;
    try {
      const url = getAdminEndpointUrl('companies', 'detail', { id: companyId });
      const data = await adminHttpClient.get<CompanyDetail>(url);
      setCompany(data);
      setSigners(data.signers || []);
      setTaxId(data.taxId || '');
      setBusinessName(data.businessName || '');
    } catch (err) {
      console.error('Error cargando empresa:', err);
    } finally {
      setLoading(false);
    }
  }

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  }

  async function handleAction(action: string, fn: () => Promise<any>) {
    setActionLoading(action);
    try {
      const result = await fn();
      showMessage('success', result?.message || 'Operacion exitosa');
      await loadCompany();
    } catch (err: any) {
      showMessage('error', err.message || 'Error en la operacion');
    } finally {
      setActionLoading('');
    }
  }

  async function uploadFileToS3(file: File): Promise<string> {
    const url = getAdminEndpointUrl('companies', 'uploadUrl');
    const { uploadUuid, endpoint } = await adminHttpClient.post<{ uploadUuid: string; endpoint: string }>(url, { contentType: file.type || 'application/octet-stream' });
    const putRes = await fetch(endpoint, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    if (!putRes.ok) throw new Error(`Error subiendo archivo a S3 (${putRes.status})`);
    return `tmp/${uploadUuid}`;
  }

  async function fetchSiiCredentials(): Promise<{ username: string } | null> {
    if (siiCredentials) return siiCredentials;
    setActionLoading('sii');
    try {
      const url = getAdminEndpointUrl('companies', 'siiCredentials', { id: companyId! });
      // El endpoint devuelve `password: '******'` literal por contrato — no lo guardamos.
      const data = await adminHttpClient.get<{ username: string; password: string }>(url);
      const next = { username: data.username };
      setSiiCredentials(next);
      return next;
    } catch (err: any) {
      showMessage('error', err.message || 'Error obteniendo credenciales SII');
      return null;
    } finally {
      setActionLoading('');
    }
  }

  /**
   * Acción exclusiva "copiar contraseña SII al portapapeles". Hace una llamada
   * puntual al endpoint dedicado, escribe al clipboard y descarta la variable
   * local sin pasarla por estado React. Si el clipboard write falla, también
   * limpia la referencia.
   */
  async function copySiiPasswordToClipboard(): Promise<void> {
    setActionLoading('sii');
    let password: string | null = null;
    try {
      const url = getAdminEndpointUrl('companies', 'siiPassword', { id: companyId! });
      const data = await adminHttpClient.get<{ password: string }>(url);
      password = data.password;
      await navigator.clipboard.writeText(password);
      setCopiedField('password');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err: any) {
      showMessage('error', err.message || 'No se pudo copiar la contraseña SII');
    } finally {
      // Borra la referencia local; el GC libera la cadena después.
      password = null;
      void password;
      setActionLoading('');
    }
  }

  async function downloadDocument(s3Key: string) {
    await handleAction('download', async () => {
      const url = getAdminEndpointUrl('companies', 'downloadDocument');
      const result = await adminHttpClient.post<{ url: string }>(url, { s3Key });
      window.open(result.url, '_blank');
      return { message: 'URL de descarga generada' };
    });
  }

  if (!companyId) return <AdminLayout title="Detalle Empresa"><div className="text-center py-16 text-slate-500">ID de empresa no especificado</div></AdminLayout>;
  if (loading) return (
    <AdminLayout title="Cargando...">
      <div className="flex items-center justify-center py-20">
        <span className="material-symbols-outlined text-3xl text-blue-500 animate-spin">progress_activity</span>
      </div>
    </AdminLayout>
  );
  if (!company) return <AdminLayout title="Detalle Empresa"><div className="text-center py-16 text-slate-500">Empresa no encontrada</div></AdminLayout>;

  return (
    <AdminLayout
      title={company.businessName || 'Sin nombre'}
      subtitle={`${company.taxId || 'Sin RUT'} — ID: ${company.id}`}
      actions={
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/empresas')} className="gap-1.5">
          <span className="material-symbols-outlined text-[16px]">arrow_back</span> Volver
        </Button>
      }
    >
      <div className="space-y-6 max-w-5xl">
        {/* Toast message */}
        {message && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
              : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
          }`}>
            <span className="material-symbols-outlined text-[18px]">{message.type === 'success' ? 'check_circle' : 'error'}</span>
            {message.text}
          </div>
        )}

        {/* Info general */}
        <SectionCard title="Informacion General" icon="info">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <InfoItem label="RUT" value={company.taxId} mono />
            <InfoItem label="Razon Social" value={company.businessName} />
            <InfoItem
              label="Tipo"
              value={
                company.companyType === 'persona_natural'
                  ? 'Persona Natural'
                  : company.companyType === 'empresa'
                    ? 'Empresa'
                    : <span className="text-amber-600 dark:text-amber-400">Sin definir</span>
              }
            />
            <InfoItem label="Email usuario" value={company.userEmail} />
            <InfoItem label="Teléfono" value={formatPhoneForDisplay(company.phone)} />
            <InfoItem label="Credenciales SII" value={company.hasSiiCredentials ? 'Registradas' : 'No registradas'} />
            <InfoItem label="Fecha registro" value={new Date(company.createdAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })} />
            <InfoItem label="Estado" value={company.enabled ? <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Activa</span> : <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" />Inactiva</span>} />
          </div>
        </SectionCard>

        {/* Etapas de activacion */}
        <SectionCard title="Etapas de Activacion" icon="checklist" description={`${ACTIVATION_STEPS.filter((s) => isStepComplete(s.key, company.activationStatus[s.key])).length} de ${ACTIVATION_STEPS.length} etapas completadas`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ACTIVATION_STEPS.map((step) => {
              const status = company.activationStatus[step.key];
              const completed = isStepComplete(step.key, status);
              return (
                <div
                  key={step.key}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    completed
                      ? 'bg-emerald-50/50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800/50'
                      : 'bg-slate-50 border-slate-200 dark:bg-slate-800/30 dark:border-slate-700'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    completed ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-slate-200 dark:bg-slate-800'
                  }`}>
                    <span className={`material-symbols-outlined text-[18px] ${completed ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                      {completed ? 'check_circle' : step.icon}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{step.label}</span>
                      {completed ? (
                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400 px-1.5 py-0.5 rounded shrink-0">Completada</span>
                      ) : (
                        <span className="text-[10px] font-medium text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-400 px-1.5 py-0.5 rounded shrink-0">Pendiente</span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                      {step.description}
                      {completed && status?.completedAt && ` · ${new Date(status.completedAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* Actualizar info — solo razón social. El RUT viene del SII y se sincroniza automáticamente. */}
        <SectionCard title="Actualizar Informacion" icon="edit" description="Modificar la razón social de la empresa. El RUT se sincroniza desde las credenciales SII y no es editable.">
          <div className="flex gap-2">
            <Input
              value={taxId || 'Sin RUT (configurar SII)'}
              readOnly
              aria-readonly
              className="w-44 h-9 text-sm font-mono bg-slate-100 dark:bg-slate-800/60 text-slate-500 cursor-not-allowed"
              title="El RUT se sincroniza desde las credenciales SII y no se puede editar manualmente"
            />
            <Input placeholder="Razon social" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="flex-1 h-9 text-sm bg-white dark:bg-slate-800" />
            <Button
              size="sm" className="h-9 text-xs bg-blue-600 hover:bg-blue-500 gap-1.5 shrink-0"
              disabled={!businessName || actionLoading === 'updateInfo'}
              onClick={() => handleAction('updateInfo', () => {
                const url = getAdminEndpointUrl('companies', 'updateInfo', { id: companyId! });
                return adminHttpClient.post(url, { businessName });
              })}
            >
              {actionLoading === 'updateInfo' ? 'Guardando...' : 'Actualizar'}
            </Button>
          </div>
        </SectionCard>

        {/* Descargar transacciones (F2 tarea 2.3) */}
        <SectionCard title="Descargar transacciones" icon="download" description="Exportar transacciones del comercio en CSV (1 fila por cuota). Útil para calcular MEL externamente o análisis ad-hoc.">
          <TransactionsExportPanel companyId={company.id} />
        </SectionCard>

        {/* Credenciales SII — segundo paso del flujo */}
        <SectionCard title="Credenciales SII" icon="lock_open" description="Usuario y clave desencriptados para uso directo">
          {!company.hasSiiCredentials ? (
            <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <span className="material-symbols-outlined text-amber-600 text-[20px]">info</span>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Esta empresa aun no ha registrado credenciales SII.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Usuario */}
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Usuario</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-mono font-medium text-slate-800 dark:text-slate-200 truncate select-all">
                    {siiCredentials ? siiCredentials.username : '••••••••••'}
                  </span>
                  <button
                    onClick={async () => {
                      const creds = siiCredentials || (await fetchSiiCredentials());
                      if (creds) {
                        navigator.clipboard.writeText(creds.username);
                        setCopiedField('username');
                        setTimeout(() => setCopiedField(null), 2000);
                      }
                    }}
                    disabled={actionLoading === 'sii'}
                    className="p-1.5 rounded-md text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors shrink-0 disabled:opacity-50"
                    title="Copiar usuario"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      {actionLoading === 'sii' ? 'progress_activity' : copiedField === 'username' ? 'check' : 'content_copy'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Clave — siempre enmascarada en pantalla. La acción "copiar" hace
                  fetch puntual al endpoint dedicado y nunca persiste el valor. */}
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Clave</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-mono font-medium text-slate-800 dark:text-slate-200 truncate select-none" aria-label="Clave SII enmascarada">
                    ••••••••••
                  </span>
                  <button
                    onClick={copySiiPasswordToClipboard}
                    disabled={actionLoading === 'sii'}
                    className="p-1.5 rounded-md text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors shrink-0 disabled:opacity-50"
                    title="Copiar clave al portapapeles"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      {actionLoading === 'sii' ? 'progress_activity' : copiedField === 'password' ? 'check' : 'content_copy'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* Documentos — solo si ya subio algun documento */}
        {(company.documents.deed.key || company.documents.powerOfAttorney.key || company.documents.identityDocument.key || company.documents.signedPromissoryNoteKey) && (
        <SectionCard title="Documentos" icon="description">
          <div className="space-y-3">
            {company.companyType === 'persona_natural' ? (
              <DocumentRow label="Documento de Identidad" status={company.documents.identityDocument.reviewStatus} fileName={company.documents.identityDocument.fileName} s3Key={company.documents.identityDocument.key} onDownload={downloadDocument} loading={actionLoading === 'download'} />
            ) : (
              <>
                <DocumentRow label="Escritura" status={company.documents.deed.reviewStatus} fileName={company.documents.deed.fileName} s3Key={company.documents.deed.key} onDownload={downloadDocument} loading={actionLoading === 'download'} />
                <DocumentRow label="Poder de Firmas" status={company.documents.powerOfAttorney.reviewStatus} fileName={company.documents.powerOfAttorney.fileName} s3Key={company.documents.powerOfAttorney.key} onDownload={downloadDocument} loading={actionLoading === 'download'} />
              </>
            )}
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[18px] text-slate-400">gavel</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Pagare Firmado</span>
              </div>
              <div className="flex items-center gap-3">
                {company.documents.signedPromissoryNoteKey ? (
                  <>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 px-2.5 py-1 rounded-full">
                      <span className="material-symbols-outlined text-[14px]">check_circle</span>Asignado
                    </span>
                    <button
                      onClick={() => downloadDocument(company.documents.signedPromissoryNoteKey!)}
                      disabled={actionLoading === 'download'}
                      className="text-xs font-medium text-blue-600 hover:text-blue-500 flex items-center gap-1 transition-colors disabled:opacity-50"
                    >
                      <span className="material-symbols-outlined text-[14px]">download</span>Descargar
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-slate-400">No asignado</span>
                )}
              </div>
            </div>
          </div>
        </SectionCard>
        )}

        {/* Firma de Documentos — solo cuando los documentos requeridos ya estan subidos */}
        {((company.companyType === 'persona_natural' && company.documents.identityDocument.key) ||
          (company.companyType === 'empresa' && company.documents.deed.key && company.documents.powerOfAttorney.key)) && (
        <SectionCard title="Firma de Documentos" icon="edit_document" description="Aprobar documentos subidos y asignar firmantes del pagare">
          {(() => {
            const alreadyApproved = company.companyType === 'persona_natural'
              ? company.documents.identityDocument.reviewStatus === 'approved'
              : company.companyType === 'empresa'
                ? company.documents.deed.reviewStatus === 'approved' && company.documents.powerOfAttorney.reviewStatus === 'approved'
                : false;

            return (
              <fieldset disabled={alreadyApproved} className="space-y-3 disabled:opacity-60">
                {alreadyApproved && (
                  <div className="flex items-center gap-2.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                    <span className="material-symbols-outlined text-emerald-600 text-[18px]">verified</span>
                    <p className="text-sm text-emerald-800 dark:text-emerald-300">
                      Documentos ya aprobados y firmantes asignados. Esta etapa esta completa.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-[9rem_1fr_1fr_2rem] gap-2 px-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  <span>RUT <span className="text-red-500">*</span></span>
                  <span>Nombre completo <span className="text-red-500">*</span></span>
                  <span>Email <span className="text-slate-400 normal-case font-normal">(opcional)</span></span>
                  <span></span>
                </div>
                {signers.map((s, i) => {
                  const rutInvalid = s.id.length > 0 && !isValidRut(s.id);
                  return (
                    <div key={i} className="grid grid-cols-[9rem_1fr_1fr_2rem] gap-2 items-center">
                      <Input
                        placeholder="12.345.678-9"
                        value={s.id}
                        onChange={(e) => { const u = [...signers]; u[i] = { ...u[i], id: formatRut(e.target.value) }; setSigners(u); }}
                        className={`h-9 text-sm font-mono bg-white dark:bg-slate-800 ${rutInvalid ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                      />
                      <Input placeholder="Juan Perez" value={s.name} onChange={(e) => { const u = [...signers]; u[i] = { ...u[i], name: e.target.value }; setSigners(u); }} className="h-9 text-sm bg-white dark:bg-slate-800" />
                      <Input placeholder="juan@empresa.cl" value={s.email} onChange={(e) => { const u = [...signers]; u[i] = { ...u[i], email: e.target.value }; setSigners(u); }} className="h-9 text-sm bg-white dark:bg-slate-800" />
                      <button
                        type="button"
                        disabled={alreadyApproved}
                        onClick={() => setSigners(signers.filter((_, j) => j !== i))}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:hover:text-slate-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                      </button>
                    </div>
                  );
                })}
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" type="button" onClick={() => setSigners([...signers, { id: '', name: '', email: '' }])} className="gap-1.5 h-8 text-xs">
                    <span className="material-symbols-outlined text-[14px]">add</span> Agregar firmante
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    className="gap-1.5 h-8 text-xs bg-blue-600 hover:bg-blue-500"
                    disabled={alreadyApproved || signers.length === 0 || signers.some((s) => !s.id || !s.name || !isValidRut(s.id)) || actionLoading === 'setup'}
                    onClick={() => handleAction('setup', () => {
                      const url = getAdminEndpointUrl('companies', 'setup', { id: companyId! });
                      return adminHttpClient.post(url, { firmantes: signers });
                    })}
                  >
                    {alreadyApproved
                      ? <><span className="material-symbols-outlined text-[14px]">check_circle</span> Aprobado</>
                      : actionLoading === 'setup'
                        ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span> Procesando...</>
                        : <><span className="material-symbols-outlined text-[14px]">check</span> Aprobar y Firmar</>}
                  </Button>
                </div>
                {!alreadyApproved && (
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 pt-1 flex items-start gap-1.5">
                    <span className="material-symbols-outlined text-[14px] text-slate-400 mt-px">info</span>
                    Esta accion aprueba los documentos subidos por el comercio y envia el pagare a los firmantes con email para su firma electronica.
                  </p>
                )}
              </fieldset>
            );
          })()}
        </SectionCard>
        )}

        {/* Aprobar pagare — solo cuando los firmantes estan listos con correo */}
        {company.signers && company.signers.length > 0 && company.signers.every((s) => !!s.email) && (
        <SectionCard title="Aprobar Pagare Firmado" icon="gavel" description="Subir el documento PDF del pagare firmado">
          {company.documents.signedPromissoryNoteKey ? (
            <div className="flex items-center justify-between gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
              <div className="flex items-center gap-2.5">
                <span className="material-symbols-outlined text-emerald-600 text-[18px]">verified</span>
                <p className="text-sm text-emerald-800 dark:text-emerald-300">El pagare firmado ya esta asignado a esta empresa.</p>
              </div>
              <button
                onClick={() => downloadDocument(company.documents.signedPromissoryNoteKey!)}
                disabled={actionLoading === 'download'}
                className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors shrink-0 disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[16px]">download</span>
                Descargar
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex items-center justify-center gap-3 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-600 rounded-lg p-6 cursor-pointer transition-colors bg-slate-50 dark:bg-slate-800/30">
                <span className="material-symbols-outlined text-[24px] text-slate-400">upload_file</span>
                <div className="text-sm">
                  {promissoryNoteFile ? (
                    <>
                      <span className="font-medium text-slate-800 dark:text-slate-200">{promissoryNoteFile.name}</span>
                      <span className="text-slate-400 ml-2">({(promissoryNoteFile.size / 1024).toFixed(1)} KB)</span>
                    </>
                  ) : (
                    <span className="text-slate-500">Selecciona el PDF del pagare firmado</span>
                  )}
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => setPromissoryNoteFile(e.target.files?.[0] || null)}
                />
              </label>
              <div className="flex gap-2">
                {promissoryNoteFile && (
                  <Button variant="outline" size="sm" type="button" className="h-8 text-xs gap-1.5" onClick={() => setPromissoryNoteFile(null)}>
                    <span className="material-symbols-outlined text-[14px]">close</span> Quitar
                  </Button>
                )}
                <Button
                  size="sm" type="button" className="h-8 text-xs bg-blue-600 hover:bg-blue-500 gap-1.5"
                  disabled={!promissoryNoteFile || actionLoading === 'promissory'}
                  onClick={() => handleAction('promissory', async () => {
                    const s3Key = await uploadFileToS3(promissoryNoteFile!);
                    const url = getAdminEndpointUrl('companies', 'approvePromissoryNotes', { id: companyId! });
                    const result = await adminHttpClient.post(url, { s3Key });
                    setPromissoryNoteFile(null);
                    return result;
                  })}
                >
                  {actionLoading === 'promissory' ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span> Subiendo...</> : <><span className="material-symbols-outlined text-[14px]">cloud_upload</span> Subir y Aprobar</>}
                </Button>
              </div>
            </div>
          )}
        </SectionCard>
        )}

        {/* Adquirentes */}
        <SectionCard title="Adquirentes" icon="credit_card">
          {company.acquirers.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No hay adquirentes conectados</p>
          ) : (
            <div className="space-y-4">
              {company.acquirers.map((acq) => {
                const mandateComplete = !!acq.cartaPoderKey && !!acq.modificacionCuentaKey;
                const slug = acq.slug || acq.acquirerId;
                const files = mandateFiles[slug] || { cartaPoder: null, modificacion: null };
                const actionKey = `mandate-${slug}`;
                return (
                  <div key={acq.acquirerId} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg overflow-hidden">
                    {/* Header del adquirente */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 flex items-center justify-center">
                          <span className="material-symbols-outlined text-[18px] text-slate-500">credit_card</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800 dark:text-white">{acq.name || acq.slug}</span>
                            <span className="text-[10px] font-medium text-slate-400 bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">{acq.integrationStatus}</span>
                            {acq.activatedAt && <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">Activado</span>}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Mandato: {mandateComplete ? <span className="text-emerald-600 font-medium">Documentos asignados</span> : <span className="text-amber-600 font-medium">Pendiente</span>}
                            {acq.lastSyncAt && ` · Sync: ${new Date(acq.lastSyncAt).toLocaleString('es-CL')}`}
                          </div>
                        </div>
                      </div>
                      {mandateComplete && (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => downloadDocument(acq.cartaPoderKey!)}
                            disabled={actionLoading === 'download'}
                            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-500 transition-colors disabled:opacity-50"
                          >
                            <span className="material-symbols-outlined text-[14px]">download</span>Carta poder
                          </button>
                          <button
                            onClick={() => downloadDocument(acq.modificacionCuentaKey!)}
                            disabled={actionLoading === 'download'}
                            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-500 transition-colors disabled:opacity-50"
                          >
                            <span className="material-symbols-outlined text-[14px]">download</span>Modif. cuenta
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Mandato pendiente: mostrar uploads */}
                    {!mandateComplete && (
                      <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900/40 space-y-3">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-[14px]">description</span>
                          Subir documentos de mandato
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Carta de Poder */}
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Carta de Poder (PDF)</label>
                            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-600 rounded-lg p-4 cursor-pointer transition-colors bg-slate-50 dark:bg-slate-800/30 min-h-[72px]">
                              <span className="material-symbols-outlined text-[20px] text-slate-400">upload_file</span>
                              <div className="text-xs">
                                {files.cartaPoder ? (
                                  <>
                                    <div className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[180px]">{files.cartaPoder.name}</div>
                                    <div className="text-slate-400">{(files.cartaPoder.size / 1024).toFixed(1)} KB</div>
                                  </>
                                ) : (
                                  <span className="text-slate-500">Seleccionar archivo</span>
                                )}
                              </div>
                              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setMandateFile(slug, 'cartaPoder', e.target.files?.[0] || null)} />
                            </label>
                          </div>

                          {/* Modificacion de Cuenta */}
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Modificacion de Cuenta (PDF)</label>
                            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-600 rounded-lg p-4 cursor-pointer transition-colors bg-slate-50 dark:bg-slate-800/30 min-h-[72px]">
                              <span className="material-symbols-outlined text-[20px] text-slate-400">upload_file</span>
                              <div className="text-xs">
                                {files.modificacion ? (
                                  <>
                                    <div className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[180px]">{files.modificacion.name}</div>
                                    <div className="text-slate-400">{(files.modificacion.size / 1024).toFixed(1)} KB</div>
                                  </>
                                ) : (
                                  <span className="text-slate-500">Seleccionar archivo</span>
                                )}
                              </div>
                              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setMandateFile(slug, 'modificacion', e.target.files?.[0] || null)} />
                            </label>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {(files.cartaPoder || files.modificacion) && (
                            <Button variant="outline" size="sm" type="button" className="h-8 text-xs gap-1.5" onClick={() => setMandateFiles((prev) => ({ ...prev, [slug]: { cartaPoder: null, modificacion: null } }))}>
                              <span className="material-symbols-outlined text-[14px]">close</span> Limpiar
                            </Button>
                          )}
                          <Button
                            size="sm" type="button" className="h-8 text-xs bg-blue-600 hover:bg-blue-500 gap-1.5"
                            disabled={!files.cartaPoder || !files.modificacion || actionLoading === actionKey}
                            onClick={() => handleAction(actionKey, async () => {
                              const [cartaPoderKey, modificacionCuentaKey] = await Promise.all([
                                uploadFileToS3(files.cartaPoder!),
                                uploadFileToS3(files.modificacion!),
                              ]);
                              const url = getAdminEndpointUrl('companies', 'approveMandateDocuments', { id: companyId! });
                              const result = await adminHttpClient.post(url, { acquirerSlug: slug, cartaPoderKey, modificacionCuentaKey });
                              setMandateFiles((prev) => ({ ...prev, [slug]: { cartaPoder: null, modificacion: null } }));
                              return result;
                            })}
                          >
                            {actionLoading === actionKey ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span> Subiendo...</> : <><span className="material-symbols-outlined text-[14px]">cloud_upload</span> Subir y Aprobar Mandato</>}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* Cuenta Bancaria — al final */}
        {company.bankAccount && (
          <SectionCard title="Cuenta Bancaria" icon="account_balance" description="Datos para recibir transferencias">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <InfoItem label="Numero de Cuenta" value={<span className="font-mono">{company.bankAccount.accountNumber}</span>} />
              <InfoItem label="Tipo de Cuenta" value={
                company.bankAccount.accountType === 'checking' ? 'Cuenta Corriente'
                : company.bankAccount.accountType === 'savings' ? 'Cuenta de Ahorro'
                : company.bankAccount.accountType === 'demand' ? 'Cuenta Vista'
                : company.bankAccount.accountType === 'rut' ? 'Cuenta RUT'
                : company.bankAccount.accountType
              } />
              <InfoItem label="Banco" value={<span className="font-mono">{company.bankAccount.bankCode}</span>} />
              <InfoItem label="RUT Titular" value={company.bankAccount.holderRut ? <span className="font-mono">{company.bankAccount.holderRut}</span> : null} />
              <InfoItem label="Email Titular" value={company.bankAccount.holderEmail} />
            </div>
          </SectionCard>
        )}

        <CompanyPausePanel companyId={company.id} />

        <CompanyCardsPanel companyId={company.id} />

        <MerchantAccountsPanel companyId={company.id} />

        <AdminMelPanel companyId={company.id} />

        <AdminAcquirerDataSection
          companyId={company.id}
          acquirers={company.acquirers.map((a) => ({ id: a.acquirerId, slug: a.slug ?? '', name: a.name ?? a.slug ?? 'Adquirente', activated: !!a.activatedAt }))}
        />
      </div>
    </AdminLayout>
  );
}

function DocumentRow({ label, status, fileName, s3Key, onDownload, loading }: {
  label: string; status: string | null; fileName: string | null; s3Key: string | null;
  onDownload: (key: string) => void; loading: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-3">
        <span className="material-symbols-outlined text-[18px] text-slate-400">draft</span>
        <div>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
          {fileName && <span className="text-[11px] text-slate-400 ml-2">{fileName}</span>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <StatusDot status={status} />
        {s3Key && (
          <button
            onClick={() => onDownload(s3Key)}
            disabled={loading}
            className="text-xs font-medium text-blue-600 hover:text-blue-500 flex items-center gap-1 transition-colors"
          >
            <span className="material-symbols-outlined text-[14px]">download</span>Descargar
          </button>
        )}
      </div>
    </div>
  );
}
